import pako from 'pako';

const LINE_NUMBER_FIELD = 'LineNumber';

/**
 * True when the form value is an AUTOMY table (array or `{ isTable }` wrapper).
 * @param {*} value
 * @returns {boolean}
 */
function isTableFormulaValue(value) {
  return Array.isArray(value) || !!(value && typeof value === 'object' && value.isTable);
}

/**
 * Serializes a form table value as a JS array literal for `safeEval`.
 * @param {*} value
 * @returns {string}
 */
function toTableFormulaLiteral(value) {
  try {
    return JSON.stringify(toRows(value));
  } catch (e) {
    return '[]';
  }
}

/*!
 * TableValue(table, valueField, filterField, filterValue)
 *
 * Returns valueField from the first ordered row that matches the equality filter.
 *
 * SAMPLES
 * TableValue({{Form.orders.Table.lines}}, 'XX', 'LineNumber', 1) -> Value 1
 * TableValue({{Form.orders.Table.lines}}, 'XX', 'BBB', 'CCC') -> Value 2
 *
 * INPUT
 * table: AUTOMY table reference {{Form.form.Table.table}}
 * valueField: Column internal name, or LineNumber for row metadata
 * filterField: Column used for equality filtering
 * filterValue: Value that must match
 *
 * OUTPUT
 * The first matching value, or empty if no row matches.
 */
function TableValue(table, valueField, filterField, filterValue) {
  if (arguments.length !== 4) return '';

  const field = readFieldName(valueField);
  const filter = readFieldName(filterField);
  if (!field || !filter) return '';

  const rows = toRows(table);
  for (let i = 0; i < rows.length; i++) {
    if (rowMatches(rows[i], filter, filterValue)) {
      return getCellValue(rows[i], field);
    }
  }

  return '';
}

/*!
 * TableExists(table, filterField, filterValue)
 *
 * Returns true when at least one ordered row matches the equality filter.
 *
 * SAMPLES
 * TableExists({{Form.orders.Table.lines}}, 'Status', 'Approved') -> True
 *
 * OUTPUT
 * True if a matching row exists, otherwise False.
 */
function TableExists(table, filterField, filterValue) {
  if (arguments.length !== 3) return false;

  const filter = readFieldName(filterField);
  if (!filter) return false;

  const rows = toRows(table);
  for (let i = 0; i < rows.length; i++) {
    if (rowMatches(rows[i], filter, filterValue)) return true;
  }

  return false;
}

/*!
 * TableCount(table)
 * TableCount(table, filterField, filterValue)
 *
 * Returns the number of rows, optionally filtered by equality.
 *
 * SAMPLES
 * TableCount({{Form.orders.Table.lines}}) -> 3
 * TableCount({{Form.orders.Table.lines}}, 'Status', 'Approved') -> 2
 */
function TableCount(table, filterField, filterValue) {
  const argCount = arguments.length;
  if (argCount !== 1 && argCount !== 3) return 0;

  const rows = toRows(table);
  if (argCount === 1) return rows.length;

  const filter = readFieldName(filterField);
  if (!filter) return 0;

  let count = 0;
  for (let i = 0; i < rows.length; i++) {
    if (rowMatches(rows[i], filter, filterValue)) count++;
  }

  return count;
}

/*!
 * TableSum(table, valueField)
 * TableSum(table, valueField, filterField, filterValue)
 *
 * Returns the numeric sum of valueField, optionally filtered by equality.
 *
 * SAMPLES
 * TableSum({{Form.orders.Table.lines}}, 'Amount') -> 60
 * TableSum({{Form.orders.Table.lines}}, 'Amount', 'Status', 'Approved') -> 50
 */
function TableSum(table, valueField, filterField, filterValue) {
  const values = collectDecimals(table, valueField, filterField, filterValue, arguments.length);
  if (!values) return 0;
  if (values.length === 0) return 0;

  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
  }

  return sum;
}

/*!
 * TableAverage(table, valueField)
 * TableAverage(table, valueField, filterField, filterValue)
 *
 * Returns the numeric average of valueField, optionally filtered by equality.
 *
 * SAMPLES
 * TableAverage({{Form.orders.Table.lines}}, 'Amount') -> 20
 * TableAverage({{Form.orders.Table.lines}}, 'Amount', 'Status', 'Approved') -> 25
 */
function TableAverage(table, valueField, filterField, filterValue) {
  const values = collectDecimals(table, valueField, filterField, filterValue, arguments.length);
  if (!values || values.length === 0) return '';

  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i];
  }

  return sum / values.length;
}

/*!
 * TableMin(table, valueField)
 * TableMin(table, valueField, filterField, filterValue)
 *
 * Returns the numeric minimum of valueField, optionally filtered by equality.
 *
 * SAMPLES
 * TableMin({{Form.orders.Table.lines}}, 'Amount') -> 10
 */
function TableMin(table, valueField, filterField, filterValue) {
  const values = collectDecimals(table, valueField, filterField, filterValue, arguments.length);
  if (!values || values.length === 0) return '';

  let min = values[0];
  for (let i = 1; i < values.length; i++) {
    if (values[i] < min) min = values[i];
  }

  return min;
}

/*!
 * TableMax(table, valueField)
 * TableMax(table, valueField, filterField, filterValue)
 *
 * Returns the numeric maximum of valueField, optionally filtered by equality.
 *
 * SAMPLES
 * TableMax({{Form.orders.Table.lines}}, 'Amount') -> 30
 */
function TableMax(table, valueField, filterField, filterValue) {
  const values = collectDecimals(table, valueField, filterField, filterValue, arguments.length);
  if (!values || values.length === 0) return '';

  let max = values[0];
  for (let i = 1; i < values.length; i++) {
    if (values[i] > max) max = values[i];
  }

  return max;
}

/*!
 * TableFirstValue(table, valueField)
 *
 * Returns valueField from the first ordered row.
 *
 * SAMPLES
 * TableFirstValue({{Form.orders.Table.lines}}, 'XX') -> Value 1
 */
function TableFirstValue(table, valueField) {
  if (arguments.length !== 2) return '';

  const field = readFieldName(valueField);
  if (!field) return '';

  const rows = toRows(table);
  if (rows.length === 0) return '';

  return getCellValue(rows[0], field);
}

/*!
 * TableLastValue(table, valueField)
 *
 * Returns valueField from the last ordered row.
 *
 * SAMPLES
 * TableLastValue({{Form.orders.Table.lines}}, 'XX') -> Value 3
 */
function TableLastValue(table, valueField) {
  if (arguments.length !== 2) return '';

  const field = readFieldName(valueField);
  if (!field) return '';

  const rows = toRows(table);
  if (rows.length === 0) return '';

  return getCellValue(rows[rows.length - 1], field);
}

function toRows(table) {
  if (table == null || table === '') return [];

  let source = table;
  if (typeof source === 'object' && !Array.isArray(source) && source.isTable) {
    source = source.value;
  }

  if (typeof Uint8Array !== 'undefined' && source instanceof Uint8Array) {
    try {
      source = JSON.parse(pako.ungzip(source, { to: 'string' }));
    } catch (e) {
      return [];
    }
  }

  if (typeof source === 'string') {
    const trimmed = source.trim();
    if (!trimmed) return [];
    try {
      source = JSON.parse(trimmed);
    } catch (e) {
      return [];
    }
  }

  if (!Array.isArray(source)) return [];

  const rows = [];
  for (let i = 0; i < source.length; i++) {
    const row = source[i];
    if (row != null && typeof row === 'object') rows.push(row);
  }

  return sortRows(rows);
}

function sortRows(rows) {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const left = getLineNumber(a.row);
      const right = getLineNumber(b.row);
      if (left !== right) return left - right;
      return a.index - b.index;
    })
    .map((item) => item.row);
}

function getLineNumber(row) {
  const raw = getLineNumberRaw(row);
  const n = Number(raw);
  return Number.isFinite(n) ? n : 0;
}

function getLineNumberRaw(row) {
  if (row == null) return undefined;
  if (row.lineNumber != null && row.lineNumber !== '') return row.lineNumber;
  if (row.LineNumber != null && row.LineNumber !== '') return row.LineNumber;
  return undefined;
}

function readFieldName(name) {
  if (name == null) return '';
  return String(name).trim();
}

function unwrapCell(cell) {
  if (cell && typeof cell === 'object' && !Array.isArray(cell) && 'value' in cell) {
    return cell.value;
  }
  return cell;
}

function getRaw(row, fieldName) {
  if (fieldName === LINE_NUMBER_FIELD) {
    const lineNumber = getLineNumberRaw(row);
    if (lineNumber != null) return lineNumber;
  }

  const nested = row?.data;
  if (nested && typeof nested === 'object' && !Array.isArray(nested) && fieldName in nested) {
    return unwrapCell(nested[fieldName]);
  }

  return unwrapCell(row?.[fieldName]);
}

function getText(row, fieldName) {
  const raw = getRaw(row, fieldName);
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'boolean') return raw ? 'true' : 'false';
  return String(raw);
}

function getCellValue(row, fieldName) {
  const raw = getRaw(row, fieldName);
  const text = getText(row, fieldName);
  if (isEmptyCell(raw, text)) return '';
  return raw;
}

function isEmptyCell(raw, text) {
  if (raw == null) return true;
  if (typeof raw === 'string') return raw.trim() === '';
  return text == null || String(text).trim() === '';
}

function getFilterText(filterValue) {
  if (filterValue == null) return '';
  if (typeof filterValue === 'string') return filterValue.trim();
  if (typeof filterValue === 'boolean') return filterValue ? 'true' : 'false';
  return String(filterValue);
}

function tryParseBoolean(raw, text) {
  if (typeof raw === 'boolean') return { ok: true, value: raw };

  const value = String(text ?? '').trim();
  if (value.toLowerCase() === 'true') return { ok: true, value: true };
  if (value.toLowerCase() === 'false') return { ok: true, value: false };

  return { ok: false, value: false };
}

function tryParseDecimal(raw, text) {
  if (typeof raw === 'boolean') return null;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;

  const source = String(text ?? '').trim();
  if (!source) return null;

  if (/^[+-]?(?:\d+\.?\d*|\.\d+)$/.test(source)) {
    const n = Number(source);
    return Number.isFinite(n) ? n : null;
  }

  if (/^[+-]?\d{1,3}(?:,\d{3})+(?:\.\d+)?$/.test(source)) {
    const n = Number(source.replace(/,/g, ''));
    return Number.isFinite(n) ? n : null;
  }

  if (/^[+-]?\d+,\d+$/.test(source)) {
    const n = Number(source.replace(',', '.'));
    return Number.isFinite(n) ? n : null;
  }

  return null;
}

function rowMatches(row, filterField, filterValue) {
  const tableRaw = getRaw(row, filterField);
  const tableText = getText(row, filterField);
  const filterText = getFilterText(filterValue);

  const leftEmpty = isEmptyCell(tableRaw, tableText);
  const rightEmpty = filterText === '';
  if (leftEmpty && rightEmpty) return true;
  if (leftEmpty || rightEmpty) return false;

  const leftBoolean = tryParseBoolean(tableRaw, tableText);
  const rightBoolean = tryParseBoolean(filterValue, filterText);
  if (leftBoolean.ok && rightBoolean.ok) return leftBoolean.value === rightBoolean.value;

  const leftDecimal = tryParseDecimal(tableRaw, tableText);
  const rightDecimal = tryParseDecimal(filterValue, filterText);
  if (leftDecimal != null && rightDecimal != null) return leftDecimal === rightDecimal;

  return tableText === filterText;
}

function collectDecimals(table, valueField, filterField, filterValue, argCount) {
  if (argCount !== 2 && argCount !== 4) return null;

  const field = readFieldName(valueField);
  if (!field) return [];

  const filter = argCount === 4 ? readFieldName(filterField) : '';
  const rows = toRows(table);
  const values = [];

  for (let i = 0; i < rows.length; i++) {
    if (argCount === 4 && !rowMatches(rows[i], filter, filterValue)) continue;

    const raw = getRaw(rows[i], field);
    const text = getText(rows[i], field);
    if (isEmptyCell(raw, text)) continue;
    if (typeof raw === 'boolean') {
      throw new Error('InvalidDecimalNumber');
    }

    const n = tryParseDecimal(raw, text);
    if (n == null) {
      throw new Error('InvalidDecimalNumber');
    }

    values.push(n);
  }

  return values;
}

export default {
  TableValue,
  TableExists,
  TableCount,
  TableSum,
  TableAverage,
  TableMin,
  TableMax,
  TableFirstValue,
  TableLastValue,
  toTableFormulaLiteral,
  isTableFormulaValue,
};
